import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, CloseIcon, BackArrow, UserAvatar, StaticAlert, UserAccountScreenSkeleton, Toggle, SkeletonCard, Loader } from './SkepticParts';
import { get_member_data, member_logout, getNewsletters, updateNewsletter, check_server_online, fetch_api, stripe_checkout_session } from '../api/api_calls';
import { CustomScroll } from "react-custom-scroll";
import PriceCard from './PriceCard';


const UserDetailsScreen = ({screenHandle, api_server_url}) => {
    const [memberData, setMemberData] = useState({name:'John Doe', email:'johndoe@example.com', subscription_name:'Tier Name', avatar: null, paid:null, comped:null});
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingBilling, setLoadingBilling] = useState(false);

    //console.log('[+] member data -> ', memberData);

    useEffect(()=>{
        console.log('[+] USER DETAIL SCREEN MOUNTED...');
        async function init(){
            try{
                let res = await get_member_data();
                const avatar_img = res.avatar_image;
                const name = res.name ?? 'John Doe';
                const email = res.email;
                const paid = res.paid;
                const subscription = res.subscriptions?.length ? res.subscriptions.filter(item=>item.status === 'active')[0] : [];
                const tier_name = paid ? subscription.tier.name : 'The Free Thinker';
                const client_stripe_id = res.subscriptions.length
                ? subscription?.customer?.id ?? ''
                : '';
                const comped = res.subscriptions.length
                ? (
                    !subscription?.id &&  // Subscription ID is empty
                    subscription?.price?.amount === 0 && // Complimentary price
                    subscription?.price?.nickname === "Complimentary" // Marked as complimentary
                )
                : false;

                setMemberData({name: name, email: email, subscription_name: tier_name, avatar: avatar_img, paid:paid, comped:comped, client_stripe_id: client_stripe_id});
                setLoading(false);
            }catch(err){
                console.log('[X] Error in get member data -> ', err.message);
                setError(err.message);
                setLoading(false);
            }
        }
        init();
    }, []);

    const logoutUser = async() => {
        await member_logout();
        window.location.reload();
    }

    const handleBillingUpdate = async() => {
        try{
            setLoadingBilling(true);
            let res = await stripe_checkout_session(api_server_url, 'billing_update', {name:memberData.subscription_name}, {client_email:memberData.email, client_stripe_id: memberData.client_stripe_id } )
            if (res.message === 'ok') {
                console.log('[+] Billing update success ', res.data);
                setTimeout(()=>{ window.location.href = res.data }, 500);
            } else {
              throw new Error(res.data);
            }  
        }catch(err){
            setError(err.message);
            setLoading(false);
            setLoadingBilling(false);
        }
    }

    return(
        <div className='max-w-[390px]'>
            {!error && loading && <UserAccountScreenSkeleton/>}

            {error && <StaticAlert title={'Error'} message={error}/>}

            {!error && !loading && (
            <>
            <header className='flex items-center'>
            <UserAvatar className={'size-[6.5rem] min-w-[6.5rem]'} remoteImageUrl={memberData.avatar}/>
            <div className='ml-[--gap-22-5]'>
                <div className='text-[#3f3f3f] dark:text-skeptic-silver text-xs block uppercase tracking-[0.72px] relative ml-4'> 
                    <span className='size-1.5 bg-green-500 rounded-full absolute -left-3 top-[3px]'></span> 
                    {memberData.subscription_name}
                </div>
                <h2 className='text_28 font-bold leading-none mt-1 capitalize text-skeptic-charcoal dark:text-skeptic-foreground-dark'>{memberData.name}</h2>
                <span className='text-[#3f3f3f] dark:text-skeptic-silver text-lg mt-1'>{memberData.email}</span>
            </div>
            </header>    
            <p className='text-[#3f3f3f] dark:text-skeptic-silver text-sm leading-[1.33] mt-7'>To update your profile picture on Skeptic, visit <a href="https://gravatar.com/" target='_blank' className='underline hover:text-skeptic-primary'>Gravatar</a> and update the image linked to the email 
                associated with your account. If you don’t have a Gravatar account yet, creating one is free and allows 
                you to manage the image displayed on your Skeptic profile.
            </p>
            <div className='flex gap-3 flex-wrap mt-7'>
            <div className='group'>
                <button className='skeptic_tag_badge_noborder' onClick={()=>screenHandle('nameedit')}>
                    <span>Edit Name</span>
                </button>
            </div>
            <div className='group'>
                <button className='skeptic_tag_badge_noborder' onClick={()=>screenHandle('subscription')}>
                    <span>Manage Subscription</span>
                </button>
            </div>
            <div className='group'>
                <button className='skeptic_tag_badge_noborder' onClick={()=>screenHandle('newsletter')}>
                    <span>Manage Newsletters</span>
                </button>
            </div>

            {memberData.comped === false && memberData.paid === true && (
                <div className='group'>
                <button className='skeptic_tag_badge_noborder' onClick={handleBillingUpdate}>
                    <span>Manage Billing</span>
                    { loadingBilling && <Loader/>}
                </button>
                </div>
            )}

            <div className='group'>
                <button className='skeptic_tag_badge_noborder' onClick={logoutUser}>
                    <span>Log Out</span>
                </button>
            </div>
        </div>
        </>
    )}
    </div>
    )
}

const EditNameScreen = () => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [error, setError] = useState(null);
    const [userName, setUserName] = useState(null);
    const [buttonState, setButtonState] = useState('default'); //State can be default, loading, disabled 
    const input_value = useRef();

    useEffect(()=>{
        async function init(){
            try{
                let member_data = await get_member_data();
                setUserName(member_data.name);
            }catch(err){
                console.log('[X] error in editScreen ', err.message);
                setError(err.message);
            }
        }
        init();
    }, [])

    const saveHandle = async() => {
        setErrorMessage(null);
        
        if(input_value.current.value.length < 3){
            setErrorMessage('Name has to be at least 3 characters long.');
            return;
        }

        setButtonState('loading');

        try{
            await updateNewsletter({name: input_value.current.value});
            setSuccessMessage('Name was updated.')
        }catch(err){
            console.log('[X] Error during name update');
            setErrorMessage(err.message);
        }finally{
            setButtonState('default');
        }
    }

    return(
        <div className='w-full max-w-[380px]'>
            {error && <StaticAlert title={'Error'} message={error}/>}
            {!error && (
            <>
            <div className='w-full flex flex-col'>
                <div className="flex flex-col w-full">
                    <label htmlFor='account_edit_name' className="contact_form_label">Your Name</label>
                    <input 
                    id='account_edit_name'
                    disabled={false} 
                    ref={input_value}
                    defaultValue={userName}
                    type="text" className="contact_form" placeholder="Your Name..."/>
                    <span className="form_underline"></span>
                </div> 
                <div>
                    {errorMessage && <small className="text-red-500 text-xs px-4 pt-1.5">{errorMessage}</small>}
                    {successMessage && <small className="text-green-500 text-xs px-4 pt-1.5">{successMessage}</small> }
               </div>
            </div>
            <div className='flex mt-7'>
                <Button className={'w-full justify-center'} state={buttonState} text={'Save Changes'} onClick={saveHandle}  />
            </div>
            </>
            )}
        </div>
    )
}


const NewsletterEditScreen = () => {
    const [newsletterData, setNewsletterData] = useState([]);
    const [error, setError] = useState(null);
    const [toggleError, setToggleError] = useState(null) //{toggle_id : id, error: 'string'}

    useEffect(()=>{
        async function init(){
            try{

                let newsletter_data = await getNewsletters();
                let member_data = await get_member_data();

                const newsletters = newsletter_data;
                const member_newsletters = member_data.newsletters;

                if(member_data && newsletter_data){                
                    let merge_newsletter_with_user_newsletter = newsletter_data.map(item => { 
                        const isSubscribed = member_newsletters.some(n => n.id === item.id);  
                        return { ...item, subscribed: isSubscribed  };
                    });
   
                    setNewsletterData(merge_newsletter_with_user_newsletter);
                }else{
                    throw new Error('Member data or newsletter data could not be fetched...');
                }

            }catch(err){
                console.log('[X] Error in get newsletters data -> ', err.message);
                setError('Error during newsletters data -> ', err.message);
            }            
        }
        init();
    }, []);

    const toggleHandleSwitcher = async(newsletter_id, toggle_state) => {
        setToggleError(null); // Clear any existing error for this toggle

        let editNewsletter = newsletterData.map(item => {
            if (item.id === newsletter_id) {
                return { ...item, subscribed: toggle_state  }; 
            }
            return item; // Return the unchanged item
        });

        setNewsletterData(editNewsletter); //optimisticly update toggle state

        //payload for member api {"newsletters":[{"id":"66f404a39362c0554a20b351"}]}
        let newsleter_arr = [];
        for(let n of editNewsletter){
            if(n.subscribed){
                newsleter_arr.push({id: n.id});
            }
        }
        try{
            await updateNewsletter({newsletters: newsleter_arr});

        }catch(err){
            console.log('[x] err update newsletters ', err.message);
            const revertedData = newsletterData.map(item =>
                item.id === newsletter_id ? { ...item, subscribed: !toggle_state } : item
            );
            setNewsletterData(revertedData);
            setToggleError({ id: newsletter_id, error: err.message });
        }
    }

    return(        
        <div className='w-full max-w-[380px] flex flex-col space-y-10'>

            {error && <StaticAlert title={'Error'} message={error}/>}

            {!error && (
                newsletterData.map((item) => (
                    <article key={item.id} className='w-full'>
                        <div>
                            <h2 className='text-base font-bold tracking-[-0.32px] text-skeptic-charcoal dark:text-skeptic-foreground-dark'>{item.name}</h2>
                            <p className='text-[#3f3f3f] dark:text-skeptic-silver text-sm leading-[1.33] mt-3'>
                                {item.description}
                            </p>
                        </div>
                        <div className='mt-3'>
                            <Toggle idx={item.id} checked={item.subscribed} toggleHandle={toggleHandleSwitcher} error_message={item.id === toggleError?.id ? toggleError?.error : null} />
                        </div>
                    </article>
                ))               
            )}
        </div>
    )
}

const SubscriptionEditScreen = ({api_server_url, ALLOWED_PLANS}) => {
    const [tiersData, setTiersData] = useState([]);
    const [memberData, setMemberData] = useState({});
    const [userData, setUserData] = useState({ member_status: '', client_email: '', client_stripe_id: '', client_stripe_sub_id: '', current_tier: '', });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    let calc_tier_names = [];
    let calc_user_level = NaN;
  
    const get_subs_names = () => {   
           const tier_names = tiersData.map(t => t.name);
           return ['anon', ...tier_names]; //Anon, Free are 0,1 indexes, additional are paid ones generated by Ghost CMS        
    }
    const get_user_level = (tier_levels_name) => {
        if(userData.member_status === ''){
            return 0; //anon user
        }
        if(userData.member_status === false){
            return 1; // Free member
        }
        if(userData.member_status === true){
            //TODO - handle situation when user does not match current tiers. e.g. it returns -1
            let tier_number = tier_levels_name.indexOf(userData.current_tier);
            if(tier_number === -1) throw new Error('Uknown Tier name, tier returned -1');
            return tier_number;
        }
    }
  
    calc_tier_names = get_subs_names();
    calc_user_level = get_user_level(calc_tier_names);


    const format_expiry_message = () => {
        let get_subsription = memberData.subscription.filter(item=>item.status === 'active')[0];
        let cancel_at_period_end = get_subsription.cancel_at_period_end;
        let period_end = get_subsription.current_period_end;
        let price_interval = get_subsription.price.nickname;
        const formattedDate = new Date(period_end).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
        if(price_interval.toLowerCase() === 'complimentary'){
            return `Your Subscription will expire on ${formattedDate}`;
        }
        if(cancel_at_period_end){
            return `Your Subscription will expire on ${formattedDate}`;
        }else{
            return `Your Subscription will renew on ${formattedDate}`;
        }
    }

    const subscription_expiry_tagline = memberData.paid ? format_expiry_message() : 'You currently have a free membership, upgrade to a paid subscription for full access.';

    useEffect(()=>{
        const init = async() => {
            try {                
                const check_server = await check_server_online(api_server_url);//Fail if server is not accesible                
                const get_tiers = await fetch_api('tiers', '&include=benefits,monthly_price,yearly_price&filter=active:true');                

                if (get_tiers.error) {
                    throw new Error(get_tiers.error);
                }
                //Filter Tiers according allowed array!!!
                let filter_tiers = [];
                for(let item of get_tiers.tiers){
                    for(let i of ALLOWED_PLANS){
                        if(i.name.toLowerCase() === item.name.toLowerCase()){
                            item.price = i.interval == 'month' ? item.monthly_price : item.yearly_price;
                            item.interval = i.interval;
                            delete item.yearly_price;
                            delete item.monthly_price;
                            filter_tiers.push(item);
                            break;
                        }
                    }                    
                }

                setTiersData(filter_tiers);
                const mem_data = await get_member_data();

                mem_data?.subscriptions?.length ? setMemberData({paid:mem_data?.paid ?? null, subscription:mem_data?.subscriptions}) : null;
                
                let activeSubsription = mem_data?.subscriptions?.length ? mem_data.subscriptions.filter(item=>item.status === 'active') : [];
                console.log('[+] Active Subsriptions -> ', activeSubsription);
                setUserData({
                    member_status: mem_data?.paid ?? '', // true -> paid, false -> free, '' -> anonymous user
                    client_email: mem_data?.email ?? '', // {{@member.email}}                  
        
                    client_stripe_id: activeSubsription.length
                      ? activeSubsription[0]?.customer?.id ?? ''
                      : '',
                    //We assume the first subscription in subs array is active one
                    client_stripe_sub_id: activeSubsription.length
                      ? activeSubsription[0]?.id ?? ''
                      : '',
                  
                    current_tier: activeSubsription.length
                      ? activeSubsription[0]?.tier?.name ?? ''
                      : '',
        
                    // Detect if the user is complimentary
                    comped: activeSubsription.length
                        ? (
                            !activeSubsription[0]?.id &&  // Subscription ID is empty
                            activeSubsription[0]?.price?.amount === 0 && // Complimentary price
                            activeSubsription[0]?.price?.nickname === "Complimentary" // Marked as complimentary
                        )
                        : false
                  });
                
            } catch (error) {
                setError(error.message); // Set the error state
            } finally {
                setLoading(false); // Set loading to false regardless of success or error
            }
        }
        init();
    }, []);
    console.log('Calc user level ', calc_user_level)
    return (
      <div className="flex h-full w-full max-w-[380px]">
        <header className="flex flex-col items-center w-full h-full pt-20">
            <div className='text-skeptic-charcoal dark:text-skeptic-foreground-dark'>{subscription_expiry_tagline}</div>
            <CustomScroll className="mt-[--gap-22-5] w-full pb-16" heightRelativeToParent='100%'>
                {loading && (
                    <>
                        <SkeletonCard className={'opacity-70 mt-3'}/>
                        <SkeletonCard className={'opacity-70 mt-3'}/>
                        <SkeletonCard className={'opacity-70 mt-3'}/>
                        <SkeletonCard className={'opacity-70 mt-3'}/>
                    </>
                )}

                {error &&  
                    <>
                    <div className='w-full'><StaticAlert title={'Error'} message={error}/></div>                    
                    <SkeletonCard className={'opacity-70 mt-3'}/>
                    <SkeletonCard className={'opacity-70 mt-3'}/>
                    <SkeletonCard className={'opacity-70 mt-3'}/>
                    <SkeletonCard className={'opacity-70 mt-3'}/>
                    </>               
                }
                {!loading && !error && (<div className='flex flex-col'>
                    {tiersData.filter((_, index) => index+1 >= calc_user_level).map(tierDetails => (                    
                    <PriceCard 
                    key={tierDetails.id} 
                    tier={tierDetails} 
                    subs_levels={calc_tier_names} 
                    user_level={calc_user_level} 
                    user_data={userData} 
                    className={'mt-3'}
                    isAccountCard={true}
                    />
                    ))}   </div>)                
                }
            </CustomScroll>
        </header>
      </div>
    );
    
}


/**
 * Main Drawer Component loading all necessary screens for Account management
 * 
 */

const UserAccountDrawer = ({api_server_url, ALLOWED_PLANS}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeScreen, setActiveScreen] = useState('account'); //account | newsletter | nameedit | subscription

    useEffect(() => {
        const handleOpenModal = (event) => {
            setIsModalOpen(true);
            setActiveScreen('account');
            document.getElementsByTagName('body')[0].style.overflow = 'hidden';       
          };      
          window.addEventListener('openUserAccountModal', handleOpenModal);      
          return () => {
            window.removeEventListener('openUserAccountModal', handleOpenModal);
          };
    }, [])

    const handleCloseDrawer = () => {
            setIsModalOpen(false);
            document.getElementsByTagName('body')[0].style.overflow = '';         
    }

    const changeScreenHandle = (screenName) => {
        console.log('[+] Screen handle run ', screenName)
        switch (screenName) {
            case 'account':
                setActiveScreen('account');
                break;
            case 'nameedit':
                setActiveScreen('nameedit');
                break;
            case 'newsletter':
                setActiveScreen('newsletter');
                break;
            case 'subscription':
                setActiveScreen('subscription');
                break;
            default:
                setActiveScreen('account');
                break;
        }
    }   

    const drawerVariants = {
        hidden: { x: '100%' }, // Start off-screen to the right
        visible: { x: 0, transition: { type: 'spring', stiffness: 500, damping: 40 } }, // Slide into view
        exit: { x: '100%', transition: { type: 'tween', duration: 0.2 } } // Slide out to the right
    };


    return(
        <>
        <AnimatePresence>
        {isModalOpen && (

        <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 left-0 top-0 w-full h-screen backdrop-blur-sm z-[991]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleCloseDrawer} // Close the drawer when clicking on the overlay
        >
        
        <motion.aside 
        onClick={(event)=>event.stopPropagation()}
        variants={drawerVariants}
        initial="hidden"
        animate={isModalOpen ? 'visible' : 'hidden'}
        exit="exit"
        className='fixed right-0 top-0 z-[51] flex justify-center border border-black border-opacity-5 dark:border-white dark:border-opacity-5 items-center rounded-l-xl h-screen w-full md:max-w-xl bg-[rgba(242,234,215,0.85)] dark:bg-[rgba(40,40,40,0.85)] shadow-xl backdrop-blur-2xl'>
        {activeScreen === 'account' && (
            <div className='absolute left-0 top-0 p-5 cursor-pointer' onClick={ handleCloseDrawer }>
            <CloseIcon className="size-8"/>
            </div>
        )}
        {activeScreen != 'account' && (
            <div className='absolute left-0 top-0 p-5 cursor-pointer' onClick={ () => changeScreenHandle('account') }>
            <BackArrow className="size-8"/>
            </div>
        )}


        {activeScreen === 'account' && <UserDetailsScreen screenHandle={changeScreenHandle} api_server_url={api_server_url}/>}  
        {activeScreen === 'nameedit' && <EditNameScreen/>}
        {activeScreen === 'newsletter' && <NewsletterEditScreen/>}
        {activeScreen === 'subscription' && <SubscriptionEditScreen api_server_url={api_server_url} ALLOWED_PLANS={ALLOWED_PLANS}/>}


        </motion.aside>
        </motion.div>
        
        )}
        </AnimatePresence>
        </>
    )
}

export default UserAccountDrawer;